/**
 * Admin Service Layer
 * Handles all API calls to backend admin endpoints
 * 
 * Usage:
 * const response = await adminAPI.dashboard(idToken);
 * const data = await response.json();
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const createHeaders = (idToken) => ({
  'Content-Type': 'application/json',
  ...(idToken && { 'Authorization': `Bearer ${idToken}` })
});

const handleResponse = async (response) => {
  if (response.ok) {
    return response;
  }
  
  // Handle error responses
  const error = await response.json().catch(() => ({}));
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Please login again');
  } else if (response.status === 403) {
    throw new Error('Access Denied - Admin privileges required');
  } else if (response.status === 429) {
    throw new Error('Too many requests - Please wait and try again');
  } else if (response.status === 503) {
    throw new Error('Service temporarily unavailable');
  } else {
    throw new Error(error.message || error.error || `Request failed with status ${response.status}`);
  }
};

// ============================================================================
// DASHBOARD
// ============================================================================

export const adminAPI = {
  dashboard: async (idToken) => {
    const response = await fetch(`${API_URL}/api/admin/dashboard`, {
      method: 'GET',
      headers: createHeaders(idToken)
    });
    return handleResponse(response);
  },

  // ============================================================================
  // PRODUCTS
  // ============================================================================

  products: {
    list: async (params, idToken) => {
      const queryString = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params).toString();
      const response = await fetch(`${API_URL}/api/admin/products?${queryString}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    },

    get: async (id, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/products/${id}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    },

    create: async (data, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/products`, {
        method: 'POST',
        headers: createHeaders(idToken),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    update: async (id, data, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/products/${id}`, {
        method: 'PUT',
        headers: createHeaders(idToken),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    delete: async (id, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/products/${id}`, {
        method: 'DELETE',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    }
  },

  // ============================================================================
  // ORDERS
  // ============================================================================

  orders: {
    list: async (params, idToken) => {
      const queryString = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params).toString();
      const response = await fetch(`${API_URL}/api/admin/orders?${queryString}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    },

    get: async (id, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/orders/${id}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    },

    update: async (id, data, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/orders/${id}`, {
        method: 'PATCH',
        headers: createHeaders(idToken),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    refund: async (id, data, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/orders/${id}/refund`, {
        method: 'POST',
        headers: createHeaders(idToken),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    createManual: async (data, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/orders/create-manual`, {
        method: 'POST',
        headers: createHeaders(idToken),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    }
  },

  // ============================================================================
  // CUSTOMERS
  // ============================================================================

  customers: {
    list: async (params, idToken) => {
      const queryString = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params).toString();
      const response = await fetch(`${API_URL}/api/admin/customers?${queryString}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    },

    get: async (id, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/customers/${id}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    }
  },

  // ============================================================================
  // TRANSACTIONS & FINANCE
  // ============================================================================

  transactions: {
    list: async (params, idToken) => {
      const queryString = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params).toString();
      const response = await fetch(`${API_URL}/api/admin/transactions?${queryString}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    }
  },

  profitLoss: async (params, idToken) => {
    const dateFrom = params.dateFrom || '';
    const dateTo = params.dateTo || '';
    const groupBy = params.groupBy || 'monthly';
    
    const response = await fetch(
      `${API_URL}/api/admin/profit-loss?dateFrom=${dateFrom}&dateTo=${dateTo}&groupBy=${groupBy}`,
      {
        method: 'GET',
        headers: createHeaders(idToken)
      }
    );
    return handleResponse(response);
  },

  // ============================================================================
  // RECONCILIATION
  // ============================================================================

  reconciliation: {
    list: async (params, idToken) => {
      const queryString = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params).toString();
      const response = await fetch(`${API_URL}/api/admin/reconciliation?${queryString}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    },

    get: async (id, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/reconciliation/${id}`, {
        method: 'GET',
        headers: createHeaders(idToken)
      });
      return handleResponse(response);
    },

    run: async (data, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/reconciliation/run`, {
        method: 'POST',
        headers: createHeaders(idToken),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    resolve: async (id, data, idToken) => {
      const response = await fetch(`${API_URL}/api/admin/reconciliation/${id}/resolve`, {
        method: 'PATCH',
        headers: createHeaders(idToken),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    }
  }
};

export default adminAPI;
